/*
 * How much is open, and how urgent, for the priority banner.
 *
 * Pure, in the shape of workOrderSearch.js and materialSource.js, so the counting can be tested
 * without rendering a banner.
 *
 * Counted in the client: the orders are already here, and a count endpoint would
 * be a second truth to keep in step with the list.
 */
import { WorkOrderPriority, isWorkOrderOpen } from "./workOrderEnums";

/*
 * Most urgent first. The banner reads left to right, so this is the order someone triages in —
 * and it lives here rather than in the component so no future edit can render Low ahead of
 * Critical by reordering some JSX.
 */
const priorityOrder = [
  WorkOrderPriority.Critical,
  WorkOrderPriority.High,
  WorkOrderPriority.Medium,
  WorkOrderPriority.Low,
];

/*
 * Open orders per priority level as [{ priority, count }], most urgent first.
 *
 * Levels with nothing open are left out rather than reported as zero: the banner renders what it
 * is given, and a strip reading "0 Critical 0 High 0 Medium 0 Low" is height spent saying nothing
 * happened. An entirely quiet register therefore returns [], which is what makes the banner
 * disappear instead of sitting there empty.
 *
 * "Open" is isWorkOrderOpen's to decide — the same answer the sidebar badge and the machine tab
 * badge use. Escalated orders are not open, so an escalated fault is counted once, on the child
 * order that inherited the work, rather than twice.
 */
export const getOpenPriorityCounts = (workOrders) => {
  const counts = new Map();

  (workOrders ?? []).forEach((workOrder) => {
    if (!isWorkOrderOpen(workOrder?.status)) return;
    const priority = workOrder.priority;
    counts.set(priority, (counts.get(priority) ?? 0) + 1);
  });

  return priorityOrder
    .filter((priority) => counts.has(priority))
    .map((priority) => ({ priority, count: counts.get(priority) }));
};
